import React, { useState } from "react";
import { Alert } from "@actinc/dls/components";
import { Plus as PlusIcon } from "@actinc/dls/icons";
import { Box, Button } from "@mui/material";
import { PropTypes } from "prop-types";
import { useFieldArray, useFormContext } from "react-hook-form";

import { ReferencesDialog } from "./ReferencesDialog";

import { AlertDialog, SimpleTable } from "../../../../components";
import { useMemoCompare } from "../../../../services/hooks";

const ReferencesTab = ({ objectType, readonly }) => {
  const { control, getValues } = useFormContext();
  const { fields, remove, replace } = useFieldArray({
    name: `${objectType}.referenceCitations`,
    control,
  });

  const [alertDialogProps, setAlertDialogProps] = useState({
    open: false,
  });
  const [dialogProps, setDialogProps] = useState({
    open: false,
  });

  const sortFn = (a, b) => a.referenceTitle.localeCompare(b.referenceTitle);

  const referenceCitations = useMemoCompare(fields.sort(sortFn));

  const handleDialogClose = (index) => (data) => {
    setDialogProps({ open: false });
    if (!data) return;
    replace(
      (getValues(`${objectType}.referenceCitations`) || [])
        .filter((_citation, i) => !Number.isInteger(index) || index !== i)
        .concat(data)
        .sort(sortFn),
    );
  };

  const tableProps = {
    actions: [
      {
        label: readonly ? "View" : "Edit",
        onClick: (row) => () => {
          setDialogProps({
            objectType,
            onClose: handleDialogClose(row.idx),
            open: true,
            readonly,
            reference: row.vals,
          });
        },
      },
      {
        disabled: readonly,
        label: "Delete",
        onClick: (row) => (e) => {
          setAlertDialogProps({
            confirmButtonText: "Delete",
            dialogTitle: "Delete Reference",
            maxWidth: "md",
            message: (
              <Alert
                severity="warning"
                sx={{ padding: 1 }}
                title={
                  <>
                    Delete Reference
                    <Box
                      component="span"
                      sx={{ fontStyle: "italic", fontWeight: "bold" }}
                    >
                      {` ${row.vals.referenceTitle}`}
                    </Box>
                    ?
                  </>
                }
              />
            ),
            onClose: (confirmed) => {
              setAlertDialogProps({ open: false });
              if (!confirmed) return;
              remove(row?.idx);
            },
            open: true,
          });
        },
      },
    ],
    columns: [
      { title: "Reference Title", identifier: "referenceTitle" },
      { title: "Work Title", identifier: "workTitle" },
    ],
    error: false,
    rows: referenceCitations,
  };

  return (
    <>
      {!readonly && (
        <Box sx={{ display: "flex", justifyContent: "end", marginBottom: 1 }}>
          <Button
            onClick={() =>
              setDialogProps({
                objectType,
                onClose: handleDialogClose(),
                open: true,
                readonly,
              })
            }
            size="small"
            startIcon={<PlusIcon />}
            variant="outlined"
          >
            Add Reference
          </Button>
        </Box>
      )}
      <SimpleTable {...tableProps} />
      {dialogProps.open && <ReferencesDialog {...dialogProps} />}
      {alertDialogProps.open && <AlertDialog {...alertDialogProps} />}
    </>
  );
};

ReferencesTab.propTypes = {
  objectType: PropTypes.string.isRequired,
  readonly: PropTypes.bool.isRequired,
};

export { ReferencesTab };
